import type { MenuProps } from 'antd';
import type { CSSProperties } from 'react';

import { changeLanguage } from '../../src/i18n';

export type MenuItem = Required<MenuProps>['items'][number];

/**
 * Options for getItem when using onClick for navigation (e.g. to avoid
 * Ant Design Menu intercepting Link clicks in static export).
 */
export interface GetItemOptions {
  onClick?: () => void;
}

/**
 * Helper function to create menu items with consistent structure.
 * Pass options.onClick for navigation items so the menu does not intercept the click.
 */
export function getItem(
  label: React.ReactNode,
  key: string,
  icon?: React.ReactNode,
  children?: MenuItem[],
  options?: GetItemOptions,
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
    ...(options?.onClick && { onClick: options.onClick }),
  } as MenuItem;
}

/**
 * Handle locale change using react-i18next
 */
export function handleLocaleChange(newLocale: string): void {
  changeLanguage(newLocale);
}

/**
 * Create language menu items for the dropdown
 */
export function createLanguageMenuItems(
  onLocaleChange: (locale: string) => void,
): MenuProps['items'] {
  return [
    {
      key: 'en',
      label: (
        <div className="flex items-center">
          <span className="mr-2 text-xl">🇺🇸</span>
          <span>English (US)</span>
        </div>
      ),
      onClick: () => onLocaleChange('en'),
    },
    {
      key: 'es',
      label: (
        <div className="flex items-center">
          <span className="mr-2 text-xl">🇪🇸</span>
          <span>Español</span>
        </div>
      ),
      onClick: () => onLocaleChange('es'),
    },
  ];
}

/**
 * Get the current locale flag emoji
 */
export function getCurrentLocaleFlag(locale: string): string {
  return locale === 'es' ? '🇪🇸' : '🇺🇸';
}

/**
 * Fixed Sider positioning styles
 */
export const FIXED_SIDER_STYLE: CSSProperties = {
  position: 'fixed',
  left: 0,
  top: 64,
  zIndex: 10,
  height: 'calc(100vh - 64px)',
  overflow: 'auto',
  background: 'var(--ant-layout-sider-bg)',
};

/**
 * Drawer configuration for mobile sidebar
 */
export const DRAWER_CONFIG = {
  placement: 'left' as const,
  width: 256,
  styles: {
    body: { padding: 0 },
  },
  className: 'lg:hidden',
};
